"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { UPDATE_UA_BIGQUERY_EVENT_PARAM_KEYS_NAME } from "./constants";
import type { UpdateUserAnalyticsBigQueryEventParamKeysInput } from "./schema";

const PreviewQuery = graphql(`
  query UaBigQueryEventParamKeysConfirmPreview($id: ID!) {
    node(id: $id) {
      __typename
      ... on UserAnalyticsBigQuery {
        id
        projectID
        datasetName
        eventParamKeys
        selectedEventParamKeys
        eventParamBackfill {
          status
          processedDays
          totalDays
          errorMessage
        }
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function UpdateUserAnalyticsBigQueryEventParamKeysConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const isTarget =
    isToolUIPart(part) &&
    getToolName(part) === UPDATE_UA_BIGQUERY_EVENT_PARAM_KEYS_NAME;
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ??
    {}) as Partial<UpdateUserAnalyticsBigQueryEventParamKeysInput>;
  const id = input.id ?? "";

  const [{ data, fetching }] = useQuery({
    query: PreviewQuery,
    variables: { id },
    pause: !(isTarget && isInputAvailable && id),
  });

  if (!isTarget || !isInputAvailable) return null;

  const node =
    data?.node?.__typename === "UserAnalyticsBigQuery" ? data.node : null;
  const nextKeys = input.selectedEventParamKeys ?? [];

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Update User Analytics event parameter keys?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>New selected keys</Trans>:{" "}
          <span className="font-mono text-xs">
            {nextKeys.length > 0 ? nextKeys.join(", ") : "—"}
          </span>
        </li>
        <li>
          <Trans>Current selected keys</Trans>:{" "}
          {fetching ? (
            <Skeleton className="inline-block h-4 w-24" />
          ) : (
            <span className="font-mono text-xs">
              {(node?.selectedEventParamKeys ?? []).length > 0
                ? (node?.selectedEventParamKeys ?? []).join(", ")
                : "—"}
            </span>
          )}
        </li>
        <li>
          <Trans>Available keys</Trans>:{" "}
          {fetching ? (
            <Skeleton className="inline-block h-4 w-24" />
          ) : (
            <span className="font-mono text-xs">
              {(node?.eventParamKeys ?? []).length > 0
                ? (node?.eventParamKeys ?? []).join(", ")
                : "—"}
            </span>
          )}
        </li>
        <li>
          <Trans>BigQuery dataset</Trans>:{" "}
          {fetching ? (
            <Skeleton className="inline-block h-4 w-24" />
          ) : (
            <span className="font-mono text-xs">
              {node ? `${node.projectID}.${node.datasetName}` : "Unknown"}
            </span>
          )}
        </li>
        <li>
          <Trans>Backfill status</Trans>:{" "}
          {fetching ? (
            <Skeleton className="inline-block h-4 w-16" />
          ) : (
            <span className="font-medium">
              {node?.eventParamBackfill.status ?? "Unknown"}
            </span>
          )}
        </li>
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
